<template>
	<Teleport to="html">
		<MultiButtonModal
			:show="showNoCCNSearchResults"
			icon="alert"
			title="Add your organization"
			:message="`Sorry, no organizations were found for the CCN ${ccn}.`"
			:buttons="[
									new ModalButtonData(
										'Ok', () => { showNoCCNSearchResults = false }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>

	<HeroLayout v-model="showContactUs">
		<div class="flex flex-col w-full h-full">
			<p class="text-center text-xl font-semibold text-heading">Step</p>
			<StepIndicator class="mt-4" :current_step="calculatedStep" :total_steps="4" />

			<p class="registration-title mt-21 text-heading">Add your organization</p>

			<p v-if="search_results" class="clickable-text my-2" @click="search_results = null">Back to search</p>

			<div v-if="!search_results">
				<div class="flex flex-row my-10 h-12">
					<p class="registration-slide-button-left"
						:class="search_method === 'cms' ? 'registration-slide-button-selected' : 'registration-slide-button-deselected'"
						@click.prevent="search_method = 'cms'">Provider Identifier</p>
					<p class="registration-slide-button-right"
						:class="search_method === 'name' ? 'registration-slide-button-selected' : 'registration-slide-button-deselected'"
						@click.prevent="search_method = 'name'">Name & Address</p>
				</div>

				<form>
					<div v-if="search_method === 'cms'">
						<InputText label="NPI, TIN or CMS Certification Number*" type="text" placeholder="Enter organization NPI, TIN, or CCN" v-model="ccn"
									  :errors="error" error-field="organization_request.ccn" :tooltip="npiTooltip" />
					</div>
					<div v-else class="flex flex-col">
						<InputText label="Organization name" type="text" placeholder="Enter organization name" v-model="org_name"
									  :errors="error" error-field="organization_request.name" />

						<InputText label="Organization address" type="text" placeholder="Enter organization address" v-model="org_address" class="mt-1"
									  :errors="error" error-field="organization_request.street" />

						<div class="flex flex-row space-x-4">
							<InputText label="City" type="text" placeholder="Enter organization city" v-model="city" class="w-full"
										  :errors="error" error-field="organization_request.city" />

							<Combobox id="combo_find_state" v-model="selected_state" :items="FormData.STATES" title="State"
										 placeholder="Select organization state"
										 class="w-full mt-0.5" :errors="error" error-field="organization_request.state" :readonly="false" />
						</div>

						<InputText label="ZIP code" type="number" placeholder="Enter 5-digit ZIP code" :max-length=5
									  v-model="zip" class="w-1/2" :errors="error" error-field="organization_request.zip_code" />
					</div>

					<div class="flex flex-col mt-10">
						<SubmitButton class="ml-auto" label="Find Provider" :loading="isLoading" @click="findProvider" />
						<router-link v-if="!search_results" :to="{ name: 'auth.add-organization-role' }" class="clickable-text ml-auto mt-3">Skip this step
						</router-link>
						<ValidationError :errors="error" :field="null" />
					</div>
				</form>
			</div>
			<div v-else>
				<div v-if="search_results.length > 0">
					<p class="registration-subtitle">Please confirm your organization below.</p>

					<fieldset class="max-h-70 overflow-y-scroll mt-10 pl-2">
						<div v-for="result in search_results" :key="result.id" class="relative flex items-start cursor-pointer h-30"
							  @click="selected_organization = result">
							<div class="flex h-6 items-center">
								<label class="container">
									<input type="radio" :checked="orgNotFound || (selected_organization && result.id === selected_organization.id)"
											 @change.prevent="selected_organization = result" :name="result.id">
									<span class="checkmark"></span>
								</label>
							</div>
							<div class="ml-9 text-sm/6">
								<p class="text-label">{{ result.organization_name }}</p>
								<p :id="`${result.id}-name`" class="text-label">{{ result.street }}</p>
								<p :id="`${result.id}-address`" class="text-label">{{ `${result.city}, ${result.state} ${result.zip}` }}</p>
								<p v-if="result.facility_type != null" :id="`${result.id}-specialty`"
									class="font-semibold text-label">{{ `Specialty: ${result.facility_type}` }}</p>
							</div>
						</div>
					</fieldset>

					<SubmitButton class="ml-auto mt-10" label="Confirm" :loading="isLoading" @click="confirmProvider" />
					<ValidationError :errors="error" :field="null" />
				</div>
			</div>
		</div>
	</HeroLayout>
</template>

<script setup lang="js">
import { computed, ref } from 'vue'
import InputText from '@/components/Forms/InputText.vue'
import ValidationError from '@/components/Forms/InputError.vue'
import StepIndicator from '@/components/Registration/StepIndicator.vue'
import { organizationsApi } from '@/api/Organizations/OrganizationsApi.js'
import Combobox from '@/components/Forms/Combobox.vue'
import { userOrganizationsApi } from '@/api/Organizations/UserOrganizationsApi.js'
import { useAuthStore, UserOrganizationData } from '@/stores/authStore.js'
import router from '@/router/router.js'
import { useSignupStore } from '@/stores/signupStore.js'
import HeroLayout from '@/layouts/HeroLayout.vue'
import { organizationRequestsApi } from '@/api/Organizations/OrganizationRequestsApi.js'
import { ApiGatewayResponse, HttpStatusCode } from '@/api/ApiGateway.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { FormData } from '@/models/enums/FormData.js'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'

const isLoading = ref(false)
const showNoCCNSearchResults = ref(false)
const error = ref(null)
const ccn = ref('')
const search_method = ref('cms')
const search_results = ref(null)
const org_name = ref('')
const org_address = ref('')
const city = ref('')
const selected_state = ref('')
const selected_organization = ref(null)
const zip = ref(null)
const showContactUs = ref(false)
const orgNotFound = ref(false)

const signupStore = useSignupStore()
const authStore = useAuthStore()
const auth = authStore.getAuth

const calculatedStep = computed(() =>
{
	return search_results.value && search_results.value.length > 0 ? 3 : 2
})

const npiTooltip = 'NPI or National Provider Identifier is a unique 10-digit number assigned to covered healthcare providers under HIPAA that accurately identifies administrative and financial electronic health transactions.<br><br>TIN or Taxpayer Identification Number is a 9-digit number the Internal Revenue Service (IRS) uses to identify healthcare providers and organizations for billing and tax reporting.<br><br>CCN or CMS Certification Number is a 6-digit number assigned by the Centers for Medicare & Medicaid Services (CMS) to healthcare providers and suppliers participating in Medicare.'

function findProvider()
{
	error.value = null
	isLoading.value = true

	const existingOrgs = authStore.userOrganizations.map((org) => org.id)

	const searchCriteria = {
		'ccn': search_method.value === 'cms' ? ccn.value : '',
		'name': search_method.value === 'name' ? org_name.value : '',
		'address': search_method.value === 'name' ? org_address.value : '',
		'city': search_method.value === 'name' ? city.value : '',
		'state': search_method.value === 'name' ? selected_state.value : '',
		'zip': search_method.value === 'name' ? zip.value : '',
	}

	organizationsApi.search(
		searchCriteria,
		'organization_name',
		10,
		existingOrgs,
		(response) =>
		{
			isLoading.value = false
			orgNotFound.value = response.length === 0

			if (response.length > 0)
			{
				search_results.value = response
				selected_organization.value = response[0]
			}
			else
			{
				handleNoSearchResults()
			}
		}, (e) =>
		{
			isLoading.value = false
			error.value = e.showErrorBanner()
		})
}

function confirmProvider()
{
	error.value = null
	isLoading.value = true

	if (!orgNotFound.value)
	{
		//todo-scott this does not fill out user's org role or title
		const orgs = []
		orgs.push(selected_organization.value)
		authStore.userOrganizationData = new UserOrganizationData(orgs, selected_organization.value.id)

		userOrganizationsApi.create(authStore.getAuth?.user(), auth?.user(), selected_organization.value.id,
			(userOrganization) =>
			{
				isLoading.value = false
				signupStore.setUserOrganization(JSON.stringify(userOrganization))
				router.push({ name: 'auth.add-organization-role' })
			}, (e) =>
			{
				isLoading.value = false
				error.value = e.showErrorBanner()
			}
		)
	}
	else
	{
		submitOrgCreationRequest()
	}
}

function submitOrgCreationRequest()
{
	error.value = null
	isLoading.value = true

	organizationRequestsApi.create(
		auth.user().id,
		org_name.value,
		org_address.value,
		city.value,
		selected_state.value,
		zip.value,
		'',
		'',
		'',
		() =>
		{
			isLoading.value = false
			error.value = null

			router.push({ name: 'auth.add-organization-role' })
		}, (e) =>
		{
			isLoading.value = false
			error.value = e.showErrorBanner()
		})
}

function handleNoSearchResults()
{
	if (org_name.value && org_address.value && city.value && selected_state.value && zip.value)
	{
		search_results.value = [{
			'id': auth.user().id,
			'organization_name': org_name.value,
			'street': org_address.value,
			'city': city.value,
			'state': selected_state.value,
			'zip': zip.value
		}]
	}
	else if (ccn.value)
	{
		showNoCCNSearchResults.value = true
	}
	else
	{
		buildLocalErrors()
	}
}

function buildLocalErrors(response)
{
	const errors = []

	const entryFields = new Map()

	if (search_method.value === 'cms')
	{
		entryFields.set('CCN', ['ccn', ccn.value])
	}
	else
	{
		entryFields.set('Name', ['name', org_name.value])
		entryFields.set('Address', ['street', org_address.value])
		entryFields.set('City', ['city', city.value])
		entryFields.set('State', ['state', selected_state.value])
		entryFields.set('ZIP code', ['zip_code', zip.value])
	}

	entryFields.forEach((value, key) =>
	{
		if (!value[1])
		{
			errors.push(
				{
					'title': 'Invalid Attribute',
					'status': 422,
					'detail': `${key} is required.`,
					'source': `organization_request.${value[0]}`
				}
			)
		}
	})

	error.value = new ApiGatewayResponse({
		'status': HttpStatusCode.ValidationError,
		'response': {
			'data': {
				'errors': errors
			}
		}
	}, response)
}

</script>

<style scoped>

/* Hide the browser's default radio button */
.container input {
	position: absolute;
	opacity: 0;
	cursor: pointer;
	height: 0;
	width: 0;
}

/* Create a custom radio button */
.checkmark {
	position: absolute;
	top: 2px;
	left: 0;
	height: 20px;
	width: 20px;
	border: 2px solid #00539B;
	background-color: #ffffff;
	border-radius: 50%;
	cursor: pointer;
}

/* When the radio button is checked, add a blue background */
.container input:checked ~ .checkmark {
	background-color: #ffffff;
}

/* Create the indicator (the dot/circle - hidden when not checked) */
.checkmark:after {
	content: "";
	position: absolute;
	display: none;
}

/* Show the indicator (dot/circle) when checked */
.container input:checked ~ .checkmark:after {
	display: block;
}

/* Style the indicator (dot/circle) */
.container .checkmark:after {
	top: 2px;
	left: 2px;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background: #00539B;
}

</style>
